<template>
  <v-card
    id="dob-children"
    class="card-height card-width pt-5"
    data-testid="dob-children"
  >
    <v-card-text
      v-for="(child, index) in validatedChildren"
      :key="index"
      :data-testid="`child-${index}`"
      class="pb-3"
    >
      <div
        v-if="index !== 0"
        class="card-break"
      />
      <v-row
        :class="`justify-space-around pl-2 pr-2 ${isMobile ? 'mt-2 flex-column' : 'align-center'}`"
      >
        <v-text-field
          @click="handleDobInputClick(index)"
          :class="textFieldClasses"
          :error="!child.dob && doneClicked"
          :model-value="child.dob"
          append-icon="expand_more"
          bg-color="white"
          data-testid="child_dob"
          label="Date of birth"
          variant="outlined"
          readonly
        />

        <v-text-field
          @click="handleCareNeededDateInputClick(index)"
          :class="`${textFieldClasses} ${isRemovableClasses}`"
          :error="!child.care_needed_date && doneClicked"
          :model-value="child.care_needed_date"
          append-icon="expand_more"
          bg-color="white"
          data-testid="child_date_care_needed"
          label="Date care needed"
          variant="outlined"
          readonly
        />

        <v-btn
          v-if="children.length > 1"
          @click="$emit('onRemoveChild', index)"
          :class="`centered delete-button ${isMobile ? 'align-start mr-0' : ''}`"
          variant="text"
          icon
        >
          <v-icon
            class="pt-0 pb-3"
            icon="delete"
          />
        </v-btn>
      </v-row>

      <DatePicker
        v-if="calendarActive(child)"
        @update:model-value="(date) => handleDateSelection(date, index)"
        :model-value="pickerValue(index)"
        :width="calendarWidth"
        data-testid="child-date-picker"
      />
    </v-card-text>

    <v-card-actions
      v-if="hasValidChild.length >= 1"
      class="child-actions"
    >
      <v-btn
        @click="addChild"
        :ripple="false"
        class="focus-very-visible"
        color="primary"
        data-testid="add-child-btn"
        variant="outlined"
      >
        {{ $bct('Add another child') }}
      </v-btn>
    </v-card-actions>

    <div
      v-if="isInvalid"
      class="pl-4 pr-4 pb-3 d-flex justify-center text-red"
    >
      {{ $bct('Please complete required fields') }}
    </div>
  </v-card>
</template>

<script setup>
import childrenSearchParam from '@/parent/services/children-search-param';
import DatePicker from '@/shared/components/DatePicker.vue';
import SearchFlags from '@/parent/services/search-flags';
import { useStore } from 'vuex';

import {
  newUtcDate,
  dateTimeToIso8601,
  iso8601DateStringToLocalDateTime,
} from '@/shared/services/date-helper';

const props = defineProps({
  children: {
    type: Array,
    default() {
      return [{ dob: null, care_needed_date: null }];
    },
  },
  defaultAgeGroups: {
    type: Array,
    default() {
      return [];
    },
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['onAddChild', 'onRemoveChild', 'onUpdateChild']);

const $bct = inject('translator');

defineExpose({
  handleDoneClick,
});

const DOB_PICKER = 'dob';
const CARE_NEEDED_PICKER = 'care_needed_date';

const doneClicked = ref(false);
const store = useStore();
const today = new Date();
const validatedChildren = ref([
  {
    dob: null,
    care_needed_date: null,
    valid: false,
    activePicker: null,
  },
]);

const hasValidChild = computed(() => {
  return validatedChildren.value.filter((child) => child.dob && child.care_needed_date);
});

const isInvalid = computed(() => {
  return (
    doneClicked.value &&
    validatedChildren.value.filter((child) => child.valid === false).length >= 1
  );
});

const isRemovableClasses = computed(() => {
  return validatedChildren.value.length > 1 && !props.isMobile ? 'use-removeable-spacing' : '';
});

const calendarWidth = computed(() => {
  return props.isMobile ? '100%' : '405';
});

const textFieldClasses = computed(() => {
  return props.isMobile ? 'custom-caret-placement' : 'search-input-width custom-caret-placement';
});

watch(
  () => props.children,
  (newVal) => {
    validateChildren(newVal);
  },
  {
    immediate: true,
    deep: true,
  },
);

function calendarActive(child) {
  return child.activePicker !== null && child.activePicker !== undefined;
}

// Called from parent component via ref
function handleDoneClick() {
  doneClicked.value = true;
  return !isInvalid.value;
}

function validateChildren(children) {
  validatedChildren.value = children.map((child) => validateChild(child));
}

function validateChild(child) {
  if (child.dob && child.care_needed_date) {
    return { ...child, valid: true, activePicker: null };
  }

  return { ...child, valid: false };
}

function pickerValue(index) {
  const child = validatedChildren.value[index];

  switch (child.activePicker) {
    case DOB_PICKER:
      return child?.dob ? iso8601DateStringToLocalDateTime(child?.dob) : null;
    case CARE_NEEDED_PICKER:
      return child?.care_needed_date
        ? iso8601DateStringToLocalDateTime(child?.care_needed_date)
        : null;
    default:
      return null;
  }
}

function handleDobInputClick(index) {
  const child = validatedChildren.value[index];
  const newChild = { ...child, activePicker: DOB_PICKER };

  validatedChildren.value.splice(index, 1, newChild);
}

function handleCareNeededDateInputClick(index) {
  const child = validatedChildren.value[index];
  const newChild = { ...child, activePicker: CARE_NEEDED_PICKER };

  validatedChildren.value.splice(index, 1, newChild);
}

function handleDateSelection(date, index) {
  const child = validatedChildren.value[index];

  switch (child.activePicker) {
    case DOB_PICKER:
      handleDobChange(date, index);
      break;
    case CARE_NEEDED_PICKER:
      handleCareNeededDateSelection(date, index);
      break;
    default:
      break;
  }
}

function handleCareNeededDateSelection(date, index) {
  const child = validatedChildren.value[index];
  child.care_needed_date = dateTimeToIso8601(date);
  handleChildChange(child, index);
}

function handleDobChange(date, index) {
  const child = validatedChildren.value[index];
  const isoDate = dateTimeToIso8601(date);
  child.dob = isoDate;

  if (newUtcDate(date) < newUtcDate(today)) {
    // Child DOB is in the past, default care needed date to today
    child.care_needed_date = dateTimeToIso8601(today);
  }

  if (newUtcDate(date) >= newUtcDate(today)) {
    // Child DOB is in the future, default care needed date to DOB
    child.care_needed_date = isoDate;
  }

  emit('onUpdateChild', child, index);
  handleChildChange(child, index);
}

function handleChildChange(child, index) {
  if (!child.dob || !child.care_needed_date) return;
  const updatedChild = childrenSearchParam({
    searchFlags: new SearchFlags(store),
    child,
    careNeededDate: child.care_needed_date,
    toArray: false,
  });

  emit('onUpdateChild', updatedChild, index);
}

function addChild() {
  const previousChild = validatedChildren.value.at(-1);

  const newChild = {
    dob: null,
    care_needed_date: previousChild?.care_needed_date || null,
  };

  // Reset the validations that show on "done" button click
  doneClicked.value = false;
  emit('onAddChild', newChild);
}
</script>

<style>
.card-height {
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: none !important;
}

.card-width {
  min-width: 440px;
}

.card-break {
  position: relative;
  top: -24px;
}

.child-actions {
  padding-top: 0px;
  padding-bottom: 0px;
}

.no-wrap {
  flex-wrap: nowrap;
  align-items: center;
}

.delete-button {
  position: relative;
  top: -10px;
}

.use-removeable-spacing {
  margin-left: 10px !important;
}

.custom-caret-placement {
  position: relative;
}

.custom-caret-placement .v-input__append {
  position: absolute;
  top: 18px;
  right: 10px;
}

#dob-children .v-input--is-label-active .v-input__prepend-outer {
  top: -10px;
  position: relative;
}

.search-input-width {
  max-width: 200px;
}

@media only screen and (max-width: 1450px) {
  .care-needed-picker {
    left: unset !important;
  }
}
</style>
